// Country mapping utility for consistent Sendcloud integration
// This handles all the variations found in the database and maps them to ISO country codes

export interface CountryInfo {
  isoCode: string;
  englishName: string;
  dutchName: string;
  sendcloudSupported: boolean;
}

/**
 * Comprehensive mapping of country variations to ISO codes
 * Based on actual data found in the database
 */
const COUNTRY_MAPPINGS: Record<string, string> = {
  // Netherlands variants (743 + 289 + 126 + 10 + 10 + 2 = 1180 total uses)
  'nederland': 'NL',
  'nederland ': 'NL', // with trailing space
  'netherlands': 'NL',
  'netherlands ': 'NL', // with trailing space
  'nl': 'NL',
  'the netherlands': 'NL',
  'holland': 'NL',

  // Belgium variants (15 + 11 + 2 + 1 = 29 total uses)
  'belgië': 'BE',
  'belgie': 'BE',
  'belgium': 'BE',
  'be': 'BE',

  // China variants (1647 + 170 + 35 + 25 + 2 + 4 + 2 + 1 = 1886 total uses)
  'china': 'CN',
  'china ': 'CN', // with trailing space
  'chine': 'CN', // French variant
  'chins': 'CN', // typo
  'cn': 'CN',
  "people's republic of china": 'CN',
  'prc': 'CN',
  // Invalid data cleanup
  'chinainfo@weavingdecor.com': 'CN', // email contamination
  'chinainfo@weavingdecor.com   ': 'CN', // with spaces

  // Hong Kong variants (7 + 5 + 2 + 2 = 16 total uses)
  'hong kong': 'HK',
  'hongkong': 'HK',
  'hk': 'HK',
  'hong kong sar': 'HK',

  // France variants (1 use)
  'frankrijk': 'FR',
  'france': 'FR',
  'fr': 'FR',

  // United States variants (3 uses)
  'verenigde staten': 'US',
  'united states': 'US',
  'usa': 'US',
  'us': 'US',
  'america': 'US',

  // Taiwan variants (1 use)
  'taiwan': 'TW',
  'tw': 'TW',
  'republic of china': 'TW',
  'roc': 'TW',

  // Germany variants
  'duitsland': 'DE',
  'germany': 'DE',
  'deutschland': 'DE',
  'de': 'DE',

  // United Kingdom variants
  'verenigd koninkrijk': 'GB',
  'united kingdom': 'GB',
  'uk': 'GB',
  'gb': 'GB',
  'great britain': 'GB',
  'england': 'GB',

  // Spain variants
  'spanje': 'ES',
  'spain': 'ES',
  'españa': 'ES',
  'es': 'ES',

  // Italy variants
  'italie': 'IT',
  'italy': 'IT',
  'italia': 'IT',
  'it': 'IT',

  // Austria variants
  'oostenrijk': 'AT',
  'austria': 'AT',
  'österreich': 'AT',
  'at': 'AT',

  // Switzerland variants
  'zwitserland': 'CH',
  'switzerland': 'CH',
  'schweiz': 'CH',
  'suisse': 'CH',
  'svizzera': 'CH',
  'ch': 'CH',

  // Denmark variants
  'denemarken': 'DK',
  'denmark': 'DK',
  'danmark': 'DK',
  'dk': 'DK',

  // Norway variants
  'noorwegen': 'NO',
  'norway': 'NO',
  'norge': 'NO',
  'no': 'NO',

  // Sweden variants
  'zweden': 'SE',
  'sweden': 'SE',
  'sverige': 'SE',
  'se': 'SE',

  // Finland variants
  'finland': 'FI',
  'suomi': 'FI',
  'fi': 'FI',

  // Poland variants
  'polen': 'PL',
  'poland': 'PL',
  'polska': 'PL',
  'pl': 'PL',

  // Czech Republic variants
  'tsjechie': 'CZ',
  'czech republic': 'CZ',
  'czechia': 'CZ',
  'česká republika': 'CZ',
  'cz': 'CZ',
};

/**
 * Country information database with Sendcloud support status
 */
const COUNTRY_INFO: Record<string, CountryInfo> = {
  'NL': {
    isoCode: 'NL',
    englishName: 'Netherlands',
    dutchName: 'Nederland',
    sendcloudSupported: true,
  },
  'BE': {
    isoCode: 'BE',
    englishName: 'Belgium',
    dutchName: 'België',
    sendcloudSupported: true,
  },
  'DE': {
    isoCode: 'DE',
    englishName: 'Germany',
    dutchName: 'Duitsland',
    sendcloudSupported: true,
  },
  'FR': {
    isoCode: 'FR',
    englishName: 'France',
    dutchName: 'Frankrijk',
    sendcloudSupported: true,
  },
  'GB': {
    isoCode: 'GB',
    englishName: 'United Kingdom',
    dutchName: 'Verenigd Koninkrijk',
    sendcloudSupported: true,
  },
  'ES': {
    isoCode: 'ES',
    englishName: 'Spain',
    dutchName: 'Spanje',
    sendcloudSupported: true,
  },
  'IT': {
    isoCode: 'IT',
    englishName: 'Italy',
    dutchName: 'Italië',
    sendcloudSupported: true,
  },
  'AT': {
    isoCode: 'AT',
    englishName: 'Austria',
    dutchName: 'Oostenrijk',
    sendcloudSupported: true,
  },
  'CH': {
    isoCode: 'CH',
    englishName: 'Switzerland',
    dutchName: 'Zwitserland',
    sendcloudSupported: true,
  },
  'DK': {
    isoCode: 'DK',
    englishName: 'Denmark',
    dutchName: 'Denemarken',
    sendcloudSupported: true,
  },
  'NO': {
    isoCode: 'NO',
    englishName: 'Norway',
    dutchName: 'Noorwegen',
    sendcloudSupported: true,
  },
  'SE': {
    isoCode: 'SE',
    englishName: 'Sweden',
    dutchName: 'Zweden',
    sendcloudSupported: true,
  },
  'FI': {
    isoCode: 'FI',
    englishName: 'Finland',
    dutchName: 'Finland',
    sendcloudSupported: true,
  },
  'PL': {
    isoCode: 'PL',
    englishName: 'Poland',
    dutchName: 'Polen',
    sendcloudSupported: true,
  },
  'CZ': {
    isoCode: 'CZ',
    englishName: 'Czech Republic',
    dutchName: 'Tsjechië',
    sendcloudSupported: true,
  },
  // Non-EU countries (limited Sendcloud support)
  'CN': {
    isoCode: 'CN',
    englishName: 'China',
    dutchName: 'China',
    sendcloudSupported: false,
  },
  'HK': {
    isoCode: 'HK',
    englishName: 'Hong Kong',
    dutchName: 'Hongkong',
    sendcloudSupported: false,
  },
  'US': {
    isoCode: 'US',
    englishName: 'United States',
    dutchName: 'Verenigde Staten',
    sendcloudSupported: false,
  },
  'TW': {
    isoCode: 'TW',
    englishName: 'Taiwan',
    dutchName: 'Taiwan',
    sendcloudSupported: false,
  },
};

/**
 * Invalid country values found in database that should be treated as errors
 */
const INVALID_COUNTRY_VALUES = new Set([
  '1', '2', '3', '4', '11', // Numbers
  'a', 'c', 'd', 'q', 'x', // Single letters
  'asd', // Random text
]);

/**
 * Convert any country input to standardized ISO country code
 * Handles all variations found in the database
 */
export function getCountryCode(countryInput: string | null | undefined): string {
  if (!countryInput || typeof countryInput !== 'string') {
    console.warn('Invalid country input:', countryInput);
    return 'NL'; // Default to Netherlands
  }

  // Clean the input: trim, lowercase, remove extra spaces
  const cleaned = countryInput.trim().toLowerCase().replace(/\s+/g, ' ');

  // Check for completely invalid values
  if (INVALID_COUNTRY_VALUES.has(cleaned)) {
    console.warn(`Invalid country value detected: "${countryInput}"`);
    return 'NL'; // Default to Netherlands
  }

  // Check for empty string after cleaning
  if (cleaned === '') {
    console.warn('Empty country input after cleaning');
    return 'NL';
  }

  // Look up in mapping
  const mappedCode = COUNTRY_MAPPINGS[cleaned];
  if (mappedCode) {
    return mappedCode;
  }

  // If input looks like a 2-letter ISO code, validate it
  const upperInput = cleaned.toUpperCase();
  if (upperInput.length === 2 && /^[A-Z]{2}$/.test(upperInput)) {
    if (COUNTRY_INFO[upperInput]) {
      return upperInput;
    }
  }

  // Log unknown country for future mapping
  console.warn(`Unknown country "${countryInput}", defaulting to NL. Please add to mapping.`);
  return 'NL'; // Default to Netherlands
}

/**
 * Get country information by ISO code
 */
export function getCountryInfo(isoCode: string): CountryInfo | null {
  return COUNTRY_INFO[isoCode.toUpperCase()] || null;
}

/**
 * Check if a country is supported by Sendcloud
 */
export function isSendcloudSupported(countryInput: string): boolean {
  const isoCode = getCountryCode(countryInput);
  const info = getCountryInfo(isoCode);
  return info?.sendcloudSupported || false;
}

/**
 * Get all supported countries for Sendcloud
 */
export function getSendcloudSupportedCountries(): CountryInfo[] {
  return Object.values(COUNTRY_INFO).filter(country => country.sendcloudSupported);
}

/**
 * Validate and clean country input for database storage
 * Returns the standardized country name or null if invalid
 */
export function validateAndCleanCountry(countryInput: string): string | null {
  const isoCode = getCountryCode(countryInput);
  const info = getCountryInfo(isoCode);
  
  if (!info) {
    return null;
  }

  // Return the English name for consistency
  return info.englishName;
}

/**
 * Get country name in specified language
 */
export function getCountryName(countryInput: string, language: 'en' | 'nl' = 'en'): string {
  const isoCode = getCountryCode(countryInput);
  const info = getCountryInfo(isoCode);
  
  if (!info) {
    return countryInput; // Return original if not found
  }

  return language === 'nl' ? info.dutchName : info.englishName;
}

/**
 * Debug function to analyze database country values
 */
export function analyzeCountryValue(countryInput: string): {
  original: string;
  cleaned: string;
  mapped: string;
  isValid: boolean;
  isSendcloudSupported: boolean;
  suggestions?: string[];
} {
  const cleaned = countryInput.trim().toLowerCase().replace(/\s+/g, ' ');
  const mapped = getCountryCode(countryInput);
  const info = getCountryInfo(mapped);
  
  return {
    original: countryInput,
    cleaned: cleaned,
    mapped: mapped,
    isValid: !!info,
    isSendcloudSupported: info?.sendcloudSupported || false,
    suggestions: info ? undefined : getSimilarCountries(cleaned),
  };
}

/**
 * Get similar country names for suggestions
 */
function getSimilarCountries(input: string): string[] {
  const suggestions: string[] = [];
  const inputLower = input.toLowerCase();
  
  // Simple similarity check
  Object.keys(COUNTRY_MAPPINGS).forEach(key => {
    if (key.includes(inputLower) || inputLower.includes(key)) {
      const isoCode = COUNTRY_MAPPINGS[key];
      const info = getCountryInfo(isoCode);
      if (info) {
        suggestions.push(info.englishName);
      }
    }
  });
  
  return [...new Set(suggestions)].slice(0, 3);
} 